import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { Animated, FlatList, ScrollView, View, TouchableOpacity, Image, ActivityIndicator } from 'react-native';
import Toast from 'react-native-root-toast';
import { generatePickListSummaryFromDockets } from '../../../common/utils/pickList/generatePickListSummary';
import { getProductImageUrl } from '../../../common/utils/products/productImageUrlResolver';
import { formatParlevelAndQuantity } from '../../../common/utils/utils';
import { ProductMeasurementUnit } from '../product';
import { Icon, Text, Toggle, TypographyParagraph, ErrorCallout } from '../../components';
import { PICK_LIST_ITEM_SET_IS_PICKED_BULK } from '../../../common/graphql/pickListItem';
import { getFullMachineName } from '../../../common/utils/machine';
import { sortPickListItems } from '../../utils/sortMethods';
import usePickListItemAction from '../../hooks/usePickListItemAction';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useTheme } from '../../theme';

const CELL_WIDTH = 120;
const CELL_HEIGHT = 60;
const PRODUCT_CELL_WIDTH = 55;
const PRODUCT_CELL_WIDTH_WITH_NAME = 160;

const PickListSummaryGrid = ({
	showAllSelections,
	onClickQuantity,
	canPick,
	selectedDocketIds,
	serviceRun,
	showProductNames,
	sortMethod,
	showExpiryWarnings,
	expiryWarningsConfig,
	onOpenExpiryWarningsDialog,
}) => {
	const { pendingItemIds, pickError, runPickAction } = usePickListItemAction();
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const [scrollPosition, setScrollPosition] = useState();
	const [scrollEvent, setScrollEvent] = useState();
	const headerScrollView = useRef(null);

	useEffect(() => {
		const scrl = new Animated.Value(0);
		setScrollPosition(scrl);
		// Animated.event returns a function
		setScrollEvent(() => Animated.event(
			[{ nativeEvent: { contentOffset: { x: scrl } } }],
			{ useNativeDriver: false },
		));
	}, []);

	useEffect(() => {
		if (scrollPosition && headerScrollView) {
			scrollPosition.addListener(position => {
				headerScrollView.current.scrollTo({ x: position.value, animated: false });
			});
		}
	}, [scrollPosition, headerScrollView]);

	const selectedDockets = useMemo(
		() => serviceRun.serviceDockets.filter(docket => selectedDocketIds.includes(docket.id)),
		[selectedDocketIds, serviceRun.serviceDockets],
	);

	const sortedItems = useMemo(() => {
		const summarisedItems = generatePickListSummaryFromDockets(
			selectedDockets,
			sortMethod.includes('machinePosition'),
			showAllSelections,
			expiryWarningsConfig,
		);

		return sortPickListItems(sortMethod, summarisedItems);
	}, [selectedDockets, sortMethod, showAllSelections, expiryWarningsConfig]);

	const [setPickListItemIsPickedBulkMutation] = useMutation(PICK_LIST_ITEM_SET_IS_PICKED_BULK);

	const setPickListItemIsPickedBulk = async (productId, machineId, pickListItemIds, isPicked) => {
		if (!canPick) {
			return;
		}

		await runPickAction(pickListItemIds, () => setPickListItemIsPickedBulkMutation({
			variables: {
				productId,
				pickListItemIds,
				isPicked,
				serviceRunId: serviceRun.id,
				machineId,
			},
		}));
	};

	const productCellWidth = showProductNames ? PRODUCT_CELL_WIDTH_WITH_NAME : PRODUCT_CELL_WIDTH;
	const productCellHeight = showProductNames ? 70 : CELL_HEIGHT;
	const bodyCellStyles = { ...styles.cell, ...styles.bodyCell, height: productCellHeight };
	const emptyCellStyles = { ...styles.cell, height: productCellHeight };

	// renders cells with pickers: non-header cells
	const renderBodyColumn = (section) => {
		const { key: docketId, machineId } = section.item;
		return (
			<View style={styles.column}>
				{
					sortedItems.map(product => {
						const key = `body-${docketId}-${product.productId}`;
						const docketValues = product.dockets[docketId];

						if (!docketValues) {
							return <View key={key} style={emptyCellStyles} />;
						}

						const {
							quantity,
							pickedQuantity,
							pickedExtraQuantity,
							extraQuantity,
							pickListItemIds,
						} = docketValues;

						return (
							<View key={key} style={bodyCellStyles}>
								<TouchableOpacity
									disabled={!onClickQuantity || pickListItemIds.some(id => pendingItemIds.includes(id))}
									onPress={() => (product.ifAnySelectedItemPicked
										? Toast.show('This item is picked. Untick it before changing its quantity.')
										: onClickQuantity(pickListItemIds))}
									style={{ flexDirection: 'row' }}
								>
									<TypographyParagraph
										href={!pickListItemIds.some(id => pendingItemIds.includes(id)) && !product.ifAnySelectedItemPicked && !!onClickQuantity}
										style={[styles.pickQuantity, extraQuantity && styles.extraQuantity]}
										text={formatParlevelAndQuantity(quantity + extraQuantity, product)}
									/>
									{/* {
										Boolean(extraQuantity) && (
											<Text style={styles.extraQuantity}>
												{ ` (+${ formatParlevelAndQuantity(extraQuantity, product) })` }
											</Text>
										)
									} */}
									<ProductMeasurementUnit product={product} />
								</TouchableOpacity>
								{
									// show toggle only when any quantity
									Boolean(quantity + extraQuantity || showAllSelections) && (
										<View>
											{pickListItemIds.some(id => pendingItemIds.includes(id)) ? (
												<ActivityIndicator style={{ width: 48, height: 48 }} accessibilityLabel="Saving picked status" />
											) : (
												<Toggle
													key={`${machineId}_${product.productId}_${quantity}`}
													onChange={() => setPickListItemIsPickedBulk(product.productId, machineId, pickListItemIds, pickedQuantity + pickedExtraQuantity !== quantity + extraQuantity)}
													partiallyEnabled={pickedQuantity + pickedExtraQuantity > 0 && pickedQuantity + pickedExtraQuantity < quantity + extraQuantity}
													enabled={pickedQuantity + pickedExtraQuantity === quantity + extraQuantity}
													disabled={!canPick || quantity + extraQuantity === 0}
												/>
											)}
										</View>
									)
								}

							</View>
						);
					})
				}
			</View>
		);
	};

	return (
		<View style={styles.container}>
			{pickError && <ErrorCallout text={pickError} />}
			<View style={styles.header}>
				<View key="topLeft" style={{ ...styles.topLeftCell, width: productCellWidth }} />
				<ScrollView
					ref={headerScrollView}
					horizontal={true}
					scrollEnabled={false}
					scrollEventThrottle={16}
				>
					{ selectedDockets.map(docket => {
						const machineName = getFullMachineName(docket.machine, docket.machine.place, docket.machine.location);

						return (
							<TouchableOpacity key={`header-${docket.id}`} onPress={() => Toast.show(machineName)} style={styles.cell}>
								<Text style={styles.cellText} numberOfLines={3}>{ machineName }</Text>
							</TouchableOpacity>
						);
					})}
				</ScrollView>
			</View>
			<FlatList
				data={[{ key: 'body' }]} // Product column
				renderItem={() => (
					<View>
						<View
							style={{
								...styles.identity,
								width: productCellWidth,
							}}
						>
							{ sortedItems.map(product => {
								const { productId, productName, productImgUrl } = product;
								const expiryWarningsStatus = product.expiryWarnings?.status;

								return (
									<View
										key={`products-${productId}`}
										style={{
											...styles.productCell,
											width: productCellWidth,
											height: productCellHeight,
										}}
									>
										{
											showExpiryWarnings && expiryWarningsStatus && expiryWarningsStatus !== 'none' && (
												<TouchableOpacity
													onPress={() => onOpenExpiryWarningsDialog && onOpenExpiryWarningsDialog(product)}
													hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
													style={styles.expiryWarningIconButton}
												>
													<Icon
														name="exclamation"
														size={16}
														color={expiryWarningsStatus === 'red' ? colors.danger : colors.warning}
													/>
												</TouchableOpacity>
											)
										}
										<TouchableOpacity onPress={() => Toast.show(productName)} style={styles.imageColumn}>
											<Image
												style={styles.productImg}
												source={{ uri: getProductImageUrl({ imageUrl: productImgUrl }) }}
											/>
											{
												showProductNames && (
													<View style={styles.productTextWrapper}>
														<Text style={styles.productText}>{productName}</Text>
													</View>
												)
											}
										</TouchableOpacity>
									</View>
								);
							})}
						</View>
						<FlatList
							style={{ marginLeft: showProductNames ? PRODUCT_CELL_WIDTH_WITH_NAME : PRODUCT_CELL_WIDTH }}
							horizontal={true}
							data={selectedDockets.map(docket => ({ key: docket.id, machineId: docket.machine.id }))}
							renderItem={renderBodyColumn}
							stickyHeaderIndices={[0]}
							onScroll={scrollEvent}
							scrollEventThrottle={16}
						/>
					</View>
				)}
			/>
		</View>
	);
};


const createStyles = ({ ui: { colors, type, space } }) => ({
	container: { flex: 1 },
	header: { flexDirection: 'row', borderTopWidth: 1, borderColor: colors.border },
	identity: { position: 'absolute', width: PRODUCT_CELL_WIDTH },
	body: { marginLeft: PRODUCT_CELL_WIDTH },
	cell: {
		width: CELL_WIDTH,
		height: CELL_HEIGHT,
		borderRightWidth: 1,
		borderBottomWidth: 1,
		borderColor: colors.border,
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
		padding: space.xxs,
		backgroundColor: colors.raised,
	},
	bodyCell: {
		justifyContent: 'flex-end',
	},
	productCell: {
		borderRightWidth: 1,
		borderBottomWidth: 1,
		borderColor: colors.border,
		width: PRODUCT_CELL_WIDTH,
		height: CELL_HEIGHT,
		padding: space.xs,
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'flex-start',
		backgroundColor: colors.raised,
	},
	expiryWarningIconButton: {
		position: 'absolute',
		top: space.xs,
		right: space.xs,
		zIndex: 1,
		width: 22,
		height: 22,
		borderRadius: 11,
		backgroundColor: colors.raised,
		borderWidth: 1,
		borderColor: colors.borderStrong,
		alignItems: 'center',
		justifyContent: 'center',
	},
	imageColumn: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'flex-start',
	},
	topLeftCell: {
		width: PRODUCT_CELL_WIDTH,
		height: CELL_HEIGHT,
		borderRightWidth: 1,
		borderBottomWidth: 1,
		borderColor: colors.border,
		backgroundColor: colors.card,
	},
	productImg: {
		height: 50,
		width: 50,
		resizeMode: 'contain', // Fits image into container with correct aspect ratio
	},
	column: { flexDirection: 'column' },
	productTextWrapper: {
		overflow: 'hidden',
		maxWidth: 100,
		marginLeft: space.xs,
	},
	pickQuantity: {
		...type.title,
		marginRight: space.md,
		color: colors.text,
	},
	extraQuantity: {
		color: colors.text,
	},
	cellText: {
		...type.caption,
		color: colors.text,
		textAlign: 'center',
	},
	productText: {
		...type.caption,
		color: colors.text,
	},
});

export default PickListSummaryGrid;
